/**
 * INITIATION_TYPES defines the enum of possible initiation types for communication.
 */
export const INITIATION_TYPES = {
  INITIATOR: 'initiator',
  RESPONDER: 'responder',
} as const;

/**
 * The InitiationType type represents the initiation type of a communication.
 * It can be either 'initiator' or 'responder', as defined in INITIATION_TYPES.
 */
export type InitiationType =
  (typeof INITIATION_TYPES)[keyof typeof INITIATION_TYPES];

export const HANDSHAKE_EVENTS = {
  INIT: 'init',
  INIT_ACK: 'init_ack',
  ACK: 'ack',
} as const;
export type HandshakeEvent =
  (typeof HANDSHAKE_EVENTS)[keyof typeof HANDSHAKE_EVENTS];

/**
 * InitiationMessage represents the structure of initiation messages used during the handshake process.
 *
 * @template Event - The type of handshake event.
 */
export interface InitiationMessage<
  Event extends HandshakeEvent = HandshakeEvent,
> {
  /**
   * The handshake event
   */
  $__event: Event;

  /**
   * unique identifier from the initiator
   */
  $__i_nonce: string;

  /**
   * unique identifier from the responder
   */
  $__r_nonce?: string;

  /**
   * The resolved communication ID
   */
  $__communicationId?: string;

  /**
   * The library unique flag. Used to identify messages from this library
   */
  $__v: '1';
}

export function isInitiationMessage(
  message: unknown,
): message is InitiationMessage {
  const _ = message as InitiationMessage;
  return (
    typeof message === 'object' &&
    message !== null &&
    typeof _.$__event === 'string' &&
    _.$__v === '1'
  );
}
